/**
 * GDPR / DPDP-compliant cookie consent banner. Renders only when the user
 * has not made a decision yet. Analytics (gtag + Meta Pixel) stay OFF until
 * the user clicks Accept — see /lib/analytics.ts initAnalytics().
 */
import React, { useEffect, useState } from "react";
import { Cookie, Shield, X } from "lucide-react";
import { getConsent, setConsent } from "@/lib/analytics";

export const ConsentBanner: React.FC = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Defer so we don't flash during SSR-like initial paint.
    const t = setTimeout(() => setShow(getConsent() === null), 400);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const decide = (v: "granted" | "denied") => {
    setConsent(v);
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="consent-banner"
      // Positioned at the BOTTOM-left of the viewport so it never occludes
      // the top-right controls (Sancta header buttons, language pill,
      // Memory brain, currency/cart cluster, dashboard link). Highest
      // z-index because dismissal is required, but leaving the top-right
      // clear means every header interaction stays clickable on first
      // paint. On desktop we cap the width so the banner stays a compact
      // corner card rather than a full-width sheet.
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-md z-[9998] rounded-2xl border border-[color:var(--sg-border)] bg-white dark:bg-[color:var(--sg-card)] text-[color:var(--sg-fg)] shadow-2xl"
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-base">Your privacy, your choice</div>
            <p className="text-xs text-[color:var(--sg-muted)] mt-1 leading-relaxed">
              We use cookies for essential features and, with your consent, Google Analytics and Meta Pixel to understand how creators use SociBaba. IP addresses are always anonymised. You can change your mind anytime from Profile settings.
            </p>
          </div>
          <button
            type="button"
            onClick={() => decide("denied")}
            aria-label="Reject non-essential"
            className="text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="consent-reject-btn"
            onClick={() => decide("denied")}
            className="px-3 py-2 rounded-full text-xs font-semibold border border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)] transition-colors"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            data-testid="consent-accept-btn"
            onClick={() => decide("granted")}
            className="px-3 py-2 rounded-full text-xs font-semibold bg-[color:var(--sg-fg)] text-white hover:bg-black dark:bg-white dark:text-[color:var(--sg-fg)] dark:hover:bg-slate-200 transition-colors inline-flex items-center justify-center gap-1"
          >
            <Shield className="h-3 w-3" /> Accept all
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
